import React, { useEffect, useState, useRef } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch } from "react-redux";
import { showSuccessSnackbar, showErrorSnackbar } from "../../redux/slices/snackbar";
import { a11yProps } from "../../utils/common";
import TabPanel from "../../components/TabPanel";
import AddressDetail from "../../components/AddressDetail";

import {
  Alert as MuiAlert,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  TextField as MuiTextField,
  Grid,
  CardHeader,
  Typography,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";
import { spacing } from "@mui/system";
import { useForm, FormProvider } from "react-hook-form";

import SaveCancel from "../../components/SaveCancel";
import useCafForm from "../../hooks/useCafForm";
import CafFields from "./components/CafFields";
import KycDocuments from "./components/KycDocuments";
import moment from "moment";
// import AddCafFormTypeFields from "./components/CafFormTypeFields";
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Card = styled(MuiCard)(spacing);

const Alert = styled(MuiAlert)(spacing);

const initialValues = {
  userName: "",
  mobileNo: "",
  emailId: "",
  fullName: "",
  routerNo: "",
  connectionType: "HOME_USER",
  plan: {},
  idProof: null,
  addressProof: null,
  doorNo: "",
  pinCode: "",
  area: "",
  city: "",
  state: "",
  country: "",
  insDoorNo: "",
  insPinCode: "",
  insArea: "",
  insCity: "",
  insState: "",
  insCountry: "",
  ipRequired: false,
  addressProofType: "",
  addressProofNo: "",
  identityProofType: "",
  identityProofNo: "",
  ipAddress: "",
  customerMileType: "",
  activationDate: "",
  activationDateStr: "",
  tenant: null,
};

const validationSchema = Yup.object().shape({
  fullName: Yup.string().required("Required"),
  userName: Yup.string()
    .required("Required")
    .matches(/^[a-zA-Z0-9,@#*._-]+$/, "Please enter valid username"),
  mobileNo: Yup.string()
    .required("Required")
    .matches(/^\d{10}$/, "Must be a valid phone number with up to 10 digits"),
  emailId: Yup.string("Enter email id")
    .required("Email id is required")
    .email("Enter a valid email"),
  plan: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .nullable()
    .required("Plan is required"),
  connectionType: Yup.string().required("Required"),
  addressProofType: Yup.string().required("Required"),
  addressProofNo: Yup.string().required("Required"),
  identityProofType: Yup.string().required("Required"),
  identityProofNo: Yup.string().required("Required"),
  ipAddress: Yup.string().when("ipRequired", {
    is: true, // ✅ if ipRequired is checked
    then: (schema) => schema.required("IP Address is required"),
    otherwise: (schema) => schema.notRequired(), // ❌ if not checked
  }),
  routerNo: Yup.string().required("Required"),
  // cafNumber: Yup.string().required("Required"),
  customerMileType: Yup.string().required("Required"),
  doorNo: Yup.string().required("Required"),
  city: Yup.string().required("Required"),
  state: Yup.string().required("Required"),
  country: Yup.string().required("Required"),
  pinCode: Yup.object().shape({
    label: Yup.string().required(),
    value: Yup.string().required(),
  }),
  insDoorNo: Yup.string().required("Required"),
  insCity: Yup.string().required("Required"),
  insState: Yup.string().required("Required"),
  insCountry: Yup.string().required("Required"),
  activationDate: Yup.string().required("Required"),
  insPinCode: Yup.object().shape({
    label: Yup.string().required(),
    value: Yup.string().required(),
  }),
  idProof: Yup.mixed()
    .required("A file is required")
    .test("fileFormat", "Only support .png, .jpeg, .jpg or .pdf", (value) => {
      return (
        value &&
        [
          "image/png", // .png
          "image/jpeg", // .jpg / .jpeg
          "application/pdf", // .pdf
        ].includes(value.type)
      );
    }),

  addressProof: Yup.mixed()
    .required("A file is required")
    .test("fileFormat", "Only support .png, .jpeg, .jpg or .pdf", (value) => {
      return (
        value &&
        [
          "image/png", // .png
          "image/jpeg", // .jpg / .jpeg
          "application/pdf", // .pdf
        ].includes(value.type)
      );
    }),
  profileProof: Yup.mixed()
    .required("A file is required")
    .test("fileFormat", "Only support .png, .jpeg, .jpg or .pdf", (value) => {
      return (
        value &&
        [
          "image/png", // .png
          "image/jpeg", // .jpg / .jpeg
          "application/pdf", // .pdf
        ].includes(value.type)
      );
    }),
  tenant: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .nullable()
    .required("Franchise is required"),
});

const CUSTOMER_DETAILS_FIELDS = [
  "userName",
  "mobileNo",
  "emailId",
  "fullName",
  "routerNo",
  "connectionType",
  "plan",
  "tenant",
  "customerMileType",
  "activationDate",
  "ipAddress",
];

const KYC_DOCUMENTS_FIELDS = [
  "idProof",
  "addressProof",
  "identityProofType",
  "identityProofNo",
  "addressProofType",
  "addressProofNo",
];

const ADDRESS_FIELDS = [
  "doorNo",
  "pinCode",
  "area",
  "city",
  "state",
  "country",
  "insDoorNo",
  "insPinCode",
  "insArea",
  "insCity",
  "insState",
  "insCountry",
];

function AddCafForm() {
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const methods = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const useTabStyles = styled({
    root: {
      justifyContent: "center",
      width: "1000px",
    },
    scroller: {
      flexGrow: "0",
    },
  });

  const classes = useTabStyles();

  const [tabValue, setTabValue] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const { state } = useLocation();
  console.log(state, "location");

  const [errorSubmit, setErrorSubmit] = useState({
    submit: "",
  });

  const { addCafForm, hasError, errorMessage, isSubmitting } = useCafForm();
  const navigate = useNavigate();

  const {
    reset,
    handleSubmit,
    formState: { errors },
    control,
    watch,
  } = methods;

  const presentAddressFields = watch(["doorNo", "pinCode", "area", "city", "state", "country"]);

  const fileInputRef = useRef(null);

  React.useEffect(() => {
    console.log(state, "statestate");
    if (
      state != null &&
      Object.keys(state).length !== 0 &&
      state.userName != undefined &&
      state.radUserId != undefined
    ) {
      const initialValues = {
        userName: {
          label: state?.userName,
          value: state?.radUserId,
        },
      };
      reset(initialValues);
    }
  }, [state]);

  const onSubmit = async (data) => {
    try {
      let formData = new FormData();

      // ✅ flatten values before sending
      data.plan = {
        planId: data.plan?.value,
        planName: data.plan?.label,
      };

      data.tenant = {
        tenantId: data.tenant?.value,
        tenantName: data.tenant?.label,
      };

      data.pinCode = data.pinCode?.label || "";
      data.insPinCode = data.insPinCode?.label || "";

      // ✅ ensure date is yyyy-MM-dd
      data.activationDate = data.activationDateStr;

      // wrap JSON
      formData.append("cafPostDto", new Blob([JSON.stringify(data)], { type: "application/json" }));
      if (data.idProof) formData.append("idProof", data.idProof);
      if (data.addressProof) formData.append("addressProof", data.addressProof);
      if (data.profileProof) formData.append("profileProof", data.profileProof);

      await addCafForm(formData);
      dispatch(showSuccessSnackbar("Caf added successfully!"));
      reset();
    } catch (err) {
      console.error(err, "caf error");
      dispatch(showErrorSnackbar(err.message));
    }
  };

  const cancelBtn = () => {
    if (state != null && Object.keys(state).length != 0) {
      navigate(state?.backTo);
    } else {
      navigate("/caf/list");
    }
  };

  const onError = (errors) => {
    const hasErrorInFields = (fields) =>
      Object.keys(errors).some((field) => fields.includes(field));

    if (hasErrorInFields(CUSTOMER_DETAILS_FIELDS)) {
      setTabValue(0);
    } else if (hasErrorInFields(KYC_DOCUMENTS_FIELDS)) {
      setTabValue(1);
    } else if (hasErrorInFields(ADDRESS_FIELDS)) {
      setTabValue(2);
    }
  };

  const breadCrump = () => {
    return (
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Typography>CafForm</Typography>
        <Typography>Add</Typography>
      </Breadcrumbs>
    );
  };

  const hasTabError = (fields) => {
    return Object.keys(errors).some((field) => fields.includes(field));
  };

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <Card mb={6}>
          <CardHeader title={t("add_caf")} subheader={breadCrump()} />
          <CardContent>
            {isSubmitting ? (
              <Box display="flex" justifyContent="center" my={6}>
                <CircularProgress />
              </Box>
            ) : (
              <form onSubmit={handleSubmit(onSubmit, onError)}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    classes={{ scroller: classes.scroller }}
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons
                    allowScrollButtonsMobile
                    aria-label="User"
                    sx={{
                      justifyContent: "center",
                      width: {
                        xs: "100%", // Full width on mobile devices
                        sm: "500px", // Fixed width on small devices
                        md: "700px", // Wider on medium devices
                        lg: "900px", // Even wider on larger devices
                      },
                      minWidth: "300px", // Ensure a minimum width across all devices
                      maxWidth: "100%", // Ensure it doesn't overflow the container
                    }}
                  >
                    {" "}
                    <Tab
                      label="Customer Details"
                      {...a11yProps(0)}
                      sx={{
                        color: hasTabError(CUSTOMER_DETAILS_FIELDS) && "error.main",
                      }}
                    />
                    <Tab
                      label="KYC Documents"
                      {...a11yProps(1)}
                      sx={{
                        color: hasTabError(KYC_DOCUMENTS_FIELDS) && "error.main",
                      }}
                    />
                    <Tab
                      label="Address "
                      {...a11yProps(2)}
                      sx={{
                        color: hasTabError(ADDRESS_FIELDS) && "error.main",
                      }}
                    />
                  </Tabs>
                  <TabPanel value={tabValue} index={0}>
                    <CafFields
                      presentAddressFields={presentAddressFields}
                      validationFields={validationSchema.fields}
                    />
                  </TabPanel>
                  <TabPanel value={tabValue} index={1}>
                    <KycDocuments
                      presentAddressFields={presentAddressFields}
                      validationFields={validationSchema.fields}
                    />
                  </TabPanel>
                  <TabPanel value={tabValue} index={2}>
                    <AddressDetail
                      presentAddressFields={presentAddressFields}
                      validationFields={validationSchema.fields}
                      oldAddress={null}
                    />
                  </TabPanel>
                </Box>

                <br></br>
                <SaveCancel cancelBtn={cancelBtn} resetForm={reset} />
              </form>
            )}
          </CardContent>
        </Card>
      </FormProvider>
    </React.Fragment>
  );
}

export default AddCafForm;
